import { HttpClient, HttpEvent, HttpHandlerFn, HttpHeaders, HttpRequest, HttpResponse, HttpStatusCode } from "@angular/common/http";
import { inject } from "@angular/core";
import { getLoggerFor } from "@tylertech/logging";
import { LOCATION, Utilities } from "@tylertech/shared-module";
import { catchError, delay, Observable, of, switchMap, tap, throwError } from "rxjs";
import { ServicePrefix, tryParseBody, TimerService } from "../services";

const trapLog = getLoggerFor("trapAndPollGatewayTimeouts");

export interface LongRunningCallResults
{
    readonly CallIsStillRunning: boolean;
}

function isLongRunningCallResults(value: unknown): value is LongRunningCallResults
{
    return !!value
        && typeof value === "object"
        && "CallIsStillRunning" in value;
}

/**
 * "if the SSL cert times out then we will go into a loop calling LongRunningCallService every 20,000 until either that call finishes and we get its return or until it is no longer running and we error out."
 *
 * @param req HttpRequest object
 * @param next the next interceptor function in the HttpClient's pipeline
 * @returns
 */
export function trapAndPollGatewayTimeouts<TReturn>(req: HttpRequest<TReturn>, next: HttpHandlerFn): Observable<HttpEvent<TReturn>>
{
    const http = inject(HttpClient);
    const timerService = inject(TimerService);
    const location = inject(LOCATION);

    const matches = /^\/(service)\/(\w+)\/(\w+)/.exec(req.url);
    if (!matches)
    {
        trapLog.debug(`external URL - by-passing trap`, { url: req.url });
        return next(req) as Observable<HttpEvent<TReturn>>;
    }

    const requestId = Utilities.generateRandomGuid();
    const reqWithHeader = req.clone({
        setHeaders: 
        {
            "RequestId": requestId
        }
    });

    const timer = timerService.create();
    timer.start();

    trapLog.debug(`Trapping req with RequestId "${requestId}" `, { url: reqWithHeader.url, requestId });
    return next(reqWithHeader)
        .pipe(
            tap(event =>
            {
                if (event instanceof HttpResponse)
                {
                    trapLog.debug(`response`, { url: reqWithHeader.url, requestId, response: event });
                }
            }),
            catchError((err) =>
            {
                const elapsed = timer.end();
                const fiveMinutes = 5 * 60 * 1_000;
                const hostName = location.hostname.toLowerCase();
                // AWS load balancer may return 504 before the 10-minute timeout, so only want to handle calls over 5 minutes
                // express-int and production will return a server header, but express-qa won't
                if (err.status === HttpStatusCode.GatewayTimeout && 
                    elapsed > fiveMinutes && 
                    !reqWithHeader.url.includes("/ILongRunningCallService/GetResults") &&
                    (err.headers?.get('server')?.startsWith('awselb/') === true || hostName === "express-qa.tylertech.com"))
                {
                    trapLog.debug(`you've activated my trap card.""`, { url: reqWithHeader.url, requestId, err });
                    return pollRequestStatus<TReturn>(http, requestId);
                }

                return throwError(() => err);
            })) as Observable<HttpEvent<TReturn>>;
}

const pollLog = getLoggerFor("pollRequestStatus");

const twentySeconds = 20_000;
function pollRequestStatus<TReturn>(http: HttpClient, requestId: string, count: number = 1): Observable<HttpResponse<TReturn>>
{
    const LongRunningRequest = http.post(
        `/${ServicePrefix.AuthorizationRequired}/ILongRunningCallService/GetResults`,
        JSON.stringify(requestId),
        {
            headers: { },
            observe: "response",
            responseType: 'blob',
        })
        .pipe(
            delay(twentySeconds),
            tap(res => { pollLog.debug(`Poll status response`, { res }); }),
            switchMap(response =>
            {
                return tryParseBody<TReturn | LongRunningCallResults>(response)
                    .pipe(
                        tap(parsedBody =>
                        {
                            pollLog.debug(`tryParseBody"`, { response, parsedBody, count });
                        }),
                        switchMap(parsedBody =>
                        {
                            const parsedResponse = new HttpResponse<TReturn | LongRunningCallResults>({
                                ...response,
                                body: parsedBody as LongRunningCallResults
                            });

                            if (isLongRunningCallResults(parsedResponse.body)
                                && parsedResponse.body.CallIsStillRunning === true)
                            {
                                pollLog.debug(`sending an other status request`, { response, count });
                                return pollRequestStatus(http, requestId, count + 1);
                            }

                            pollLog.debug(`call completed`, { response, count });
                            return of(parsedResponse as HttpResponse<TReturn>);
                        }));
            }),
            tap(res => { pollLog.debug(`Poll status processed response`, { res }); }),
        );

    return LongRunningRequest as Observable<HttpResponse<TReturn>>;
}
